import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { openWhatsApp, PTM_MSG } from "@/lib/site";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/content", label: "Content" },
  { to: "/gallery", label: "Gallery" },
  { to: "/results", label: "Results" },
  { to: "/about", label: "About Us" },
  { to: "/visit", label: "Visit Us" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/95 py-2 shadow-[0_12px_40px_-18px_rgba(28,35,51,0.35)]"
            : "bg-cream/85 py-4"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 sm:gap-4">
            <Logo size={scrolled ? "h-12 w-12 text-xl" : "h-14 w-14 text-2xl sm:h-16 sm:w-16"} className="transition-all duration-500" />
            <div className="leading-tight">
              <p data-testid="nav-brand-name" className="font-display text-2xl font-extrabold tracking-tight text-forest sm:text-3xl lg:text-4xl">Goyal Academy</p>
              <p data-testid="nav-tagline" className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-terra sm:block lg:text-[11px]">An Investment In Knowledge Pays The Best Interest.</p>
            </div>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-sm font-semibold tracking-wide transition-colors duration-300 ${
                    isActive ? "text-forest after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-full after:bg-terra" : "text-charcoal/60 hover:text-forest"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-ptm-btn"
              onClick={() => openWhatsApp(PTM_MSG)}
              className="hidden items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-bold text-forest shadow-[0_8px_24px_-10px_rgba(14,90,71,0.8)] transition-all duration-300 hover:scale-[1.04] hover:bg-[#0A4636] sm:flex"
            >
              <CalendarCheck className="h-4 w-4" strokeWidth={2.2} />
              PTM Appointment
            </button>
            <button
              data-testid="nav-mobile-menu-btn"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/15 text-forest lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] flex flex-col bg-forest"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <Logo size="h-8 w-8 text-sm" />
                <p className="font-display text-lg font-semibold text-cream">Goyal Academy</p>
              </div>
              <button data-testid="mobile-menu-close-btn" onClick={() => setOpen(false)} aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-start justify-center gap-2 px-8" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.07, duration: 0.5 }}>
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) => `font-display text-4xl font-medium ${isActive ? "text-terra" : "text-cream/80"}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                data-testid="mobile-ptm-btn"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => openWhatsApp(PTM_MSG)}
                className="mt-10 flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-forest"
              >
                <CalendarCheck className="h-4 w-4" strokeWidth={2.2} />
                PTM Appointment
              </motion.button>
            </nav>
            <p className="px-8 pb-8 font-display text-sm italic text-cream/40">An Investment In Knowledge Pays The Best Interest.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
